/**
 * Zero — the brand mark, as the thing you operate.
 *
 * A 24-hour ring the visitor can drag, arrow-key or tap. Each stop is one real
 * capability at that hour with its published cost beside it (see zeroDay.ts —
 * every number is imported from the rate card, never typed).
 *
 * WHY A RING AND NOT A CAROUSEL. The mark is already a zero and the product is
 * already sold by the month, so the shape carries the argument: a day goes
 * round once, the work accumulates, and the total lands on exactly the
 * allocation one hire includes. It is the one visual only this company could
 * use.
 *
 * ACCESSIBILITY IS THE DESIGN, NOT A RETROFIT. The ring is a slider: arrows
 * step an hour, Home/End jump to the ends, and the current stop is announced
 * through a live region, so the whole thing works with no pointer at all.
 * Under prefers-reduced-motion the sweep never auto-plays and only the
 * visitor moves it.
 *
 * Pure inline SVG on purpose: no canvas, no motion library, no new dependency.
 */

import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { dayFor, hourToAngle, type DayStop, type Vertical } from './zeroDay';
import DayStrip from './DayStrip';
import { media } from '../../styles/breakpoints';
import { WU_LABEL } from '../../config/workUnits';

const R = 108;          // ring radius
const CX = 130;
const CY = 130;
const STOP_R = 6.5;

const drift = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const Frame = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1.75rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 1.1rem;
  }
`;

const Dial = styled.svg`
  /* Below the tablet breakpoint the DAY STRIP is the control instead — see
     DayStrip.tsx. display:none (not visibility or opacity) is load-bearing: it
     takes the hidden presentation out of the accessibility tree AND out of tab
     order, so there is never a second slider for the same day. (No backticks
     in here — this is inside a template literal and they would end it.) */
  display: none;
  ${media.tablet} {
    display: block;
  }
  width: 260px;
  height: 260px;
  touch-action: none;
  cursor: grab;
  &:active { cursor: grabbing; }
  border-radius: 50%;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.45);
  }

  @media (max-width: 900px) {
    width: 216px;
    height: 216px;
  }
`;

const Sweep = styled.g<{ $playing: boolean }>`
  transform-origin: ${CX}px ${CY}px;
  ${({ $playing }) =>
    $playing &&
    css`
      animation: ${drift} 96s linear infinite;
    `}
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Readout = styled.div`
  min-width: 0;
  max-width: 30rem;

  @media (max-width: 900px) {
    text-align: center;
  }
`;

const Clock = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.14em;
  color: var(--brand-text-secondary);
  text-transform: none;
`;

const What = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.05rem, 2.4vw, 1.42rem);
  line-height: 1.32;
  color: var(--brand-text);
  margin: 0.3rem 0 0.55rem;
  text-wrap: balance;
`;

const Cost = styled.div`
  font-size: 0.87rem;
  color: var(--brand-text-secondary);
`;

const Free = styled.span`
  color: var(--brand-gold-text);
  font-weight: 600;
`;

const Hint = styled.div`
  font-size: 0.78rem;
  color: var(--brand-text-placeholder);
  margin-top: 0.9rem;
`;

const Live = styled.span`
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0);
  white-space: nowrap; border: 0;
`;

function polar(hour: number, radius = R) {
  const a = (hourToAngle(hour) * Math.PI) / 180;
  return { x: CX + radius * Math.cos(a), y: CY + radius * Math.sin(a) };
}

function clockLabel(hour: number): string {
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}${hour < 12 ? 'am' : 'pm'}`;
}

export interface ZeroProps {
  vertical: Vertical;
  /** Auto-advance through the stops until the visitor takes over. */
  autoPlay?: boolean;
}

export const Zero: React.FC<ZeroProps> = ({ vertical, autoPlay = true }) => {
  const stops = React.useMemo(() => dayFor(vertical), [vertical]);
  const [index, setIndex] = React.useState(0);
  const [touched, setTouched] = React.useState(false);
  const svgRef = React.useRef<SVGSVGElement | null>(null);

  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const playing = autoPlay && !touched && !reduceMotion;

  // Advance slowly until the visitor takes over; then it is theirs.
  React.useEffect(() => {
    if (!playing) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % stops.length), 3200);
    return () => window.clearInterval(id);
  }, [playing, stops.length]);

  // A vertical change re-scores the day, so start it again from the top.
  React.useEffect(() => setIndex(0), [vertical]);

  const current: DayStop = stops[Math.min(index, stops.length - 1)];

  const take = (next: number) => {
    setTouched(true);
    setIndex(((next % stops.length) + stops.length) % stops.length);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const k = e.key;
    if (k === 'ArrowRight' || k === 'ArrowUp') { e.preventDefault(); take(index + 1); }
    else if (k === 'ArrowLeft' || k === 'ArrowDown') { e.preventDefault(); take(index - 1); }
    else if (k === 'Home') { e.preventDefault(); take(0); }
    else if (k === 'End') { e.preventDefault(); take(stops.length - 1); }
  };

  /** Pointer anywhere on the dial selects the nearest stop. */
  const fromPointer = (clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    const deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
    const hour = (((deg / 360) * 24) % 24 + 24) % 24;
    let best = 0;
    let bestGap = Infinity;
    stops.forEach((s, i) => {
      const gap = Math.min(Math.abs(s.hour - hour), 24 - Math.abs(s.hour - hour));
      if (gap < bestGap) { bestGap = gap; best = i; }
    });
    take(best);
  };

  const cursor = polar(current.hour);

  /**
   * THE SLIDER CONTRACT, BUILT ONCE. The ring and the day strip are two
   * presentations of a single control, so the role, the range, the announced
   * value and the keys are defined here and spread onto whichever one CSS is
   * showing. Written out twice they would drift, and the half that drifted
   * would be the phone — the half nobody tests by hand.
   */
  const sliderProps = {
    role: 'slider' as const,
    tabIndex: 0,
    'aria-label': 'a day in the life of a Prest0n — drag or use arrow keys',
    'aria-valuemin': 0,
    'aria-valuemax': stops.length - 1,
    'aria-valuenow': index,
    'aria-valuetext': `${clockLabel(current.hour)} — ${current.label}`,
    onKeyDown,
  };

  return (
    <Frame>
      <Dial
        ref={svgRef}
        viewBox="0 0 260 260"
        {...sliderProps}
        onPointerDown={(e) => {
          (e.target as Element).setPointerCapture?.(e.pointerId);
          fromPointer(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) fromPointer(e.clientX, e.clientY);
        }}
      >
        {/* the ring itself — the brand's zero */}
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--brand-line)" strokeWidth="1.5" />
        <circle cx={CX} cy={CY} r={R - 13} fill="none" stroke="var(--brand-line)" strokeWidth="0.75" opacity="0.55" />

        {/* hour ticks, heavier at 12 / 6 */}
        <Sweep $playing={playing}>
          {Array.from({ length: 24 }, (_, h) => {
            const outer = polar(h, R + 5);
            const inner = polar(h, h % 6 === 0 ? R - 6 : R - 2);
            return (
              <line
                key={h}
                x1={outer.x} y1={outer.y} x2={inner.x} y2={inner.y}
                stroke="var(--brand-line-strong)"
                strokeWidth={h % 6 === 0 ? 1.4 : 0.7}
                opacity={h % 6 === 0 ? 0.9 : 0.45}
              />
            );
          })}
        </Sweep>

        {/* the stops */}
        {stops.map((s, i) => {
          const p = polar(s.hour);
          const on = i === index;
          return (
            <g key={s.hour} onClick={() => take(i)} style={{ cursor: 'pointer' }}>
              <circle cx={p.x} cy={p.y} r={STOP_R + 7} fill="transparent" />
              <circle
                cx={p.x} cy={p.y} r={on ? STOP_R : STOP_R - 2.5}
                fill={on ? 'var(--brand-gold)' : 'var(--brand-canvas)'}
                stroke={on ? 'var(--brand-gold)' : 'var(--brand-line-strong)'}
                strokeWidth="1.5"
              />
            </g>
          );
        })}

        {/* the hand */}
        <line x1={CX} y1={CY} x2={cursor.x} y2={cursor.y} stroke="var(--brand-gold)" strokeWidth="1.6" strokeLinecap="round" opacity="0.85" />
        <circle cx={CX} cy={CY} r="3.2" fill="var(--brand-gold)" />

        {/* the hour, centred */}
        <text
          x={CX} y={CY + 34}
          textAnchor="middle"
          fontFamily="'Space Grotesk', sans-serif"
          fontSize="15"
          fill="var(--brand-text-secondary)"
        >
          {clockLabel(current.hour)}
        </text>
      </Dial>

      {/* The same day, unrolled, for phones. Only one of these two is ever
          present to a user — the swap is `display: none` on each. */}
      <DayStrip
        stops={stops}
        index={index}
        onPick={take}
        clockLabel={clockLabel}
        sliderProps={sliderProps}
      />

      <Readout>
        <Clock>{clockLabel(current.hour)}</Clock>
        <What>{current.label}</What>
        <Cost>
          {current.wu === 0 ? (
            <><Free>free</Free> — variance is our risk, not your invoice</>
          ) : (
            <>{current.wu} {WU_LABEL} {current.unit} — the published rate</>
          )}
        </Cost>
        <Hint>drag the ring, or use the arrow keys</Hint>
        <Live aria-live="polite">{`${clockLabel(current.hour)}. ${current.label}.`}</Live>
      </Readout>
    </Frame>
  );
};

export default Zero;
